import { z } from "zod";

import { logAndThrow } from "@/src/lib/logging/logger";
import { enrichedContentUnionSchema } from "@/src/models/blocks/enriched-content";
import { Classification } from "@/src/models/constants/classifications";
import { DatabaseDefaults } from "@/src/models/database-defaults";

export enum SectionStatus {
  PROCESSED = "processed",
  FAILED = "failed",
  EXTRACTING = "extracting",
  INVALID = "invalid",
  NO_EXTRACT = "no_extract", // No OCR was performed on the section
}

/**
 * Provenance of a section: which intake flow (standard, intake agent) produced it.
 */
export enum SectionSource {
  STANDARD = "standard", // Existing classify/extract intake pipeline
  INTAKE = "presync", // New intake agent flow (DB value kept as "presync" for backwards compat)
}

export const DEFAULT_CLASSIFICATIONS_TO_EXTRACT = [
  Classification.INTAKE,
  Classification.REFERRAL,
  Classification.REFERER_INFORMATION,
  Classification.RESPONSE_TO_VH_REQUEST,
];

export const classificationConfidenceSchema = z.record(z.nativeEnum(Classification), z.number());
export const splitDocumentConfidenceSchema = z.record(
  z.nativeEnum(Classification),
  z.object({
    pages: z.array(z.number()),
    confidence: z.number(),
  })
);

export type ClassificationConfidenceDict = z.infer<typeof classificationConfidenceSchema>;
export type SplitDocumentConfidenceDict = z.infer<typeof splitDocumentConfidenceSchema>;

export const sectionMetadataSchema = z.object({
  groupedClassifications: z.array(z.nativeEnum(Classification)).optional(),
});

export const boundingBoxSchema = z.object({
  id: z.string(),
  bbox: z.object({
    xywh: z.array(z.number()),
  }),
  page: z.number(),
  content: z.string(),
});

export const extractionMetadataSchema = z.object({
  bboxes: z.array(boundingBoxSchema),
  confidence: z.string(),
  annotation_ids: z.array(z.string()),
});

export const sectionSchema = z.object({
  pathToFile: z.string(),
  filename: z.string(),
  contentType: z.string(),
  checksum: z.string(),
  status: z.nativeEnum(SectionStatus),
  classification: z.nativeEnum(Classification),
  fileSize: z.number(),
  processedContent: z.any().nullable(),
  processedContentMetadata: z.any().nullable(),
  enrichedContent: enrichedContentUnionSchema,
  reviewedContent: z.any().nullable(),
  classificationConfidence: classificationConfidenceSchema.nullable(),
  splitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  reviewedSplitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  batchOperationName: z.string().nullable(),
  batchExtractId: z.string().nullable(),
  extractId: z.string().nullable(),
  fileId: z.string().nullable(),
  vlmProcessedContent: z.any().nullable(),
  geminiProcessedContent: z.any().nullable(),
  pages: z.array(z.number()),
  ehrDocumentId: z.string().nullable(),
  ehrDocumentType: z.string().nullable(),
  metadata: sectionMetadataSchema.nullable(),
  intakeClassification: z.nativeEnum(Classification).nullable(),
  uploadClassification: z.nativeEnum(Classification).nullable(),
  intakePathToFile: z.string().nullable(),
  uploadPathToFile: z.string().nullable(),
  intakeSectionId: z.string().nullable(),
  uploadSectionId: z.string().nullable(),
  intakePages: z.array(z.number()).nullable(),
  uploadPages: z.array(z.number()).nullable(),
  isDuplicate: z.boolean().default(false),
  hasMultiplePatients: z.boolean().default(false),
  completedAt: z.string().nullable(),
  extractionGroundTruth: z.any().nullable(),
  source: z.nativeEnum(SectionSource).default(SectionSource.STANDARD),
});

export type Section = z.infer<typeof sectionSchema> & DatabaseDefaults;
export type ExtractionMetadata = z.infer<typeof extractionMetadataSchema>;
export type BoundingBox = z.infer<typeof boundingBoxSchema>;

export function requireUploadClassification(section: Section): Classification {
  if (section.uploadClassification) return section.uploadClassification;

  logAndThrow("Section is missing required uploadClassification", {
    groupId: section.groupId,
    sectionId: section.id,
    classification: section.classification,
  });
}
